import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Factory, Thermometer, PackageCheck, Truck, Play, Clock, Droplets } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
export function ManufacturingProcess() {
  return <section className="py-24 lg:py-32 bg-[#FAF9F6] relative overflow-hidden" data-id="element-2511">
      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-2512">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-2513">
          <EnhancedScrollReveal data-id="element-2514">
            <Badge variant="secondary" className="mb-6" data-id="element-2515">
              Inside The Kitchen
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6" data-id="element-2516">
              Cocina de alta tecnología, <br data-id="element-2517" />
              <span className="text-primary-600" data-id="element-2518">corazón artesanal</span>
            </h2>
            <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-2519">
              Visitamos nuestra cocina central en Madrid para mostrarte cómo
              transformamos ingredientes frescos en salud para tu mascota.
            </p>
          </EnhancedScrollReveal>
        </div>

        {/* Immersive Scroll Container */}
        <div className="relative rounded-[3rem] overflow-hidden shadow-2xl shadow-charcoal-900/10 bg-white border border-gray-200" data-id="element-2520">
          {/* Parallax Background Image Placeholder */}
          <div className="absolute inset-0 bg-gray-100" data-id="element-2521">
            <img src="https://images.unsplash.com/photo-1556910103-1c02745a30bf?auto=format&fit=crop&q=80&w=2000" alt="Cocina Petiora" className="w-full h-full object-cover opacity-20" data-id="element-2522" />
            <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white" data-id="element-2523" />
          </div>

          <div className="relative z-10 p-8 md:p-16 lg:p-24 space-y-32" data-id="element-2524">
            {/* Step 1 */}
            <EnhancedScrollReveal className="flex flex-col md:flex-row items-center gap-12" data-id="element-2525">
              <div className="md:w-1/2" data-id="element-2526">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center text-orange-600 mb-6 shadow-sm" data-id="element-2527">
                  <Factory className="w-8 h-8" data-id="element-2528" />
                </div>
                <h3 className="text-3xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-2529">
                  Recepción y Selección
                </h3>
                <p className="text-lg text-charcoal-600 leading-relaxed mb-6" data-id="element-2530">
                  Cada mañana recibimos carne fresca y verduras de proveedores
                  locales. Inspeccionamos visualmente y por temperatura cada
                  entrega. Si no es apto para humanos, no entra.
                </p>
                <ul className="space-y-3" data-id="element-2531">
                  <li className="flex items-center gap-3 text-charcoal-700 font-medium" data-id="element-2532">
                    <Clock className="w-5 h-5 text-orange-500" data-id="element-2533" /> Procesado en
                    menos de 24h
                  </li>
                  <li className="flex items-center gap-3 text-charcoal-700 font-medium" data-id="element-2534">
                    <Droplets className="w-5 h-5 text-orange-500" data-id="element-2535" /> Lavado con
                    agua ozonizada
                  </li>
                </ul>
              </div>
              <div className="md:w-1/2" data-id="element-2536">
                <div className="aspect-video rounded-2xl bg-gray-200 overflow-hidden shadow-lg relative group cursor-pointer" data-id="element-2537">
                  <img src="https://images.unsplash.com/photo-1606850780554-b55ea684fe84?auto=format&fit=crop&q=80&w=800" alt="Ingredientes frescos" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-2538" />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2539">
                    <div className="bg-white/90 backdrop-blur rounded-full px-6 py-3 font-bold text-sm" data-id="element-2540">
                      Ver Proceso
                    </div>
                  </div>
                </div>
              </div>
            </EnhancedScrollReveal>

            {/* Step 2 */}
            <EnhancedScrollReveal className="flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-2541">
              <div className="md:w-1/2" data-id="element-2542">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center text-red-600 mb-6 shadow-sm" data-id="element-2543">
                  <Thermometer className="w-8 h-8" data-id="element-2544" />
                </div>
                <h3 className="text-3xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-2545">
                  Cocción de Precisión
                </h3>
                <p className="text-lg text-charcoal-600 leading-relaxed mb-6" data-id="element-2546">
                  Usamos hornos de vapor de última generación. La temperatura
                  nunca supera los 80°C en el corazón del producto, garantizando
                  la seguridad microbiológica sin desnaturalizar las proteínas.
                </p>
                <div className="bg-white/80 backdrop-blur p-4 rounded-xl border border-red-100 inline-block" data-id="element-2547">
                  <div className="text-3xl font-bold text-red-500 font-mono" data-id="element-2548">
                    80.0°C
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-2549">
                    Temp. Constante
                  </div>
                </div>
              </div>
              <div className="md:w-1/2" data-id="element-2550">
                <div className="aspect-video rounded-2xl bg-gray-200 overflow-hidden shadow-lg relative group" data-id="element-2551">
                  <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800" alt="Horno industrial" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-2552" />
                </div>
              </div>
            </EnhancedScrollReveal>

            {/* Step 3 */}
            <EnhancedScrollReveal className="flex flex-col md:flex-row items-center gap-12" data-id="element-2553">
              <div className="md:w-1/2" data-id="element-2554">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-6 shadow-sm" data-id="element-2555">
                  <PackageCheck className="w-8 h-8" data-id="element-2556" />
                </div>
                <h3 className="text-3xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-2557">
                  Abatimiento y Envasado
                </h3>
                <p className="text-lg text-charcoal-600 leading-relaxed mb-6" data-id="element-2558">
                  El choque térmico a -30°C bloquea el proceso de envejecimiento
                  del alimento. Envasamos al vacío inmediatamente para evitar la
                  oxidación.
                </p>
                <Button variant="outline" className="rounded-full" data-id="element-2559">
                  Ver Certificado de Seguridad <Play className="w-4 h-4 ml-2" data-id="element-2560" />
                </Button>
              </div>
              <div className="md:w-1/2" data-id="element-2561">
                <div className="aspect-video rounded-2xl bg-gray-200 overflow-hidden shadow-lg relative group" data-id="element-2562">
                  <img src="https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=800" alt="Envasado" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-2563" />
                </div>
              </div>
            </EnhancedScrollReveal>
          </div>
        </div>
      </div>
    </section>;
}